import equal from 'fast-deep-equal';
import {query} from '@di-nxt/query';
import assert from 'assert-ts';
import {produce} from 'immer';
import {getMergedTasksWithEditState} from '../../../functions';
import {
  InitialScheduleEditState,
  ScheduleEditState,
  TaskWithEditInfo,
} from '../../../types';
import {DragDropAction} from './dragDropActions';

/**
 * Reducer for the edit state transition operations.
 * @param state
 * @param action
 */
export const dragDropReducer = (
  state: ScheduleEditState = InitialScheduleEditState,
  action: DragDropAction,
): ScheduleEditState => {
  return produce(state, draft => {
    // if (action.type !== 'hover') {
    //   logAction(action, state);
    // }
    switch (action.type) {
      case 'cancelEdit': {
        return InitialScheduleEditState;
      }
      case 'beginDrag': {
        const {taskId, originalTasks} = action.payload;
        // Find task to edit
        const task = assert(
          getTaskBeforeDrag(taskId, draft.editedTasks, originalTasks),
          `Begin drag operation: Task with id ${taskId} was not found.`,
        );

        draft.currentTaskEdit = {
          ...task,
          editMode: 'dragging',
        };

        draft.currentPlaceholders = [draft.currentTaskEdit];

        return draft;
      }
      case 'cancelDrag': {
        draft.currentTaskEdit = undefined;
        draft.currentPlaceholders = [];
        return draft;
      }
      case 'beginHover':
      case 'hover': {
        const {taskId, shiftId, taskSequence, originalTasks} = action.payload;
        const current = assertCurrentEdit(draft, action);

        // Hovering in 'same' position
        if (
          current.shiftId === shiftId &&
          current.taskSequence === taskSequence &&
          current.editMode === 'hovering'
        ) {
          return draft;
        }

        // Find task being edited, original or prev. edit
        const task = assert(
          getTaskBeforeDrag(taskId, draft.editedTasks, originalTasks),
          `Begin hover / hover operation: Task with id ${taskId} was not found.`,
        );

        draft.currentTaskEdit = {
          ...current,
          shiftId,
          taskSequence:
            taskSequence !== undefined ? taskSequence : current.taskSequence,
          editMode: 'hovering',
        };

        // Hovering over same shift / unassigned
        if (task.shiftId === shiftId) {
          draft.currentPlaceholders = [draft.currentTaskEdit];
        } else {
          draft.currentPlaceholders = [
            {
              ...task,
              editMode: 'dragging',
            },
            draft.currentTaskEdit,
          ];
        }

        return draft;
      }
      case 'endHover': {
        const {taskId} = action.payload;
        const current = draft.currentTaskEdit;

        if (current && current.editMode === 'hovering') {
          draft.currentTaskEdit = {
            ...current,
            shiftId: undefined,
            editMode: 'dragging',
          };

          const placeholder = assert(
            getTaskBeforeDrag(
              taskId,
              draft.editedTasks,
              action.payload.originalTasks,
            ),
            `End hover operation: Task with id ${taskId} was not found.`,
          );

          draft.currentPlaceholders = [{...placeholder, editMode: 'dragging'}];
        }

        return draft;
      }
      case 'drop': {
        const {taskId, shiftId, taskSequence, originalTasks} = action.payload;

        // // Find original task
        const originalTask = assert(
          originalTasks.find(t => t.id === taskId),
          `Drop operation: Task with id ${taskId} was not found.`,
        );

        draft.editedTasks[taskId] = {
          ...originalTask,
          shiftId,
          taskSequence,
          editMode: 'dropped',
        };
        draft.currentTaskEdit = undefined;
        draft.currentPlaceholders = [];

        // Same shift or back to unassigned?
        // - Compare original tasks and tasks with edit to determine state is equal orignal?
        if (shiftId === originalTask.shiftId) {
          const originalShiftTaskIds = query(originalTasks)
            .where(
              t =>
                t.shiftId === shiftId && t.distrDate === originalTask.distrDate,
            )
            .orderBy(t => t.taskSequence)
            .select(t => t.id)
            .toArray();

          const tasksWithNewEdit = getMergedTasksWithEditState(
            draft,
            originalTasks,
          );
          const editedShiftTaskIds = query(tasksWithNewEdit)
            .where(
              t =>
                t.shiftId === shiftId && t.distrDate === originalTask.distrDate,
            )
            .orderBy(t => t.taskSequence)
            .select(t => t.id)
            .toArray();

          if (equal(originalShiftTaskIds, editedShiftTaskIds)) {
            originalShiftTaskIds.forEach(id => {
              if (draft.editedTasks[id]) {
                delete draft.editedTasks[id];
              }
            });
          }
        }

        return draft;
      }
      default:
        return draft;
    }
  });
};

const getTaskBeforeDrag = (
  taskId: number,
  editedTasks: {[id: number]: TaskWithEditInfo},
  originalTasks: Readonly<TaskWithEditInfo[]>,
): TaskWithEditInfo | undefined =>
  editedTasks[taskId] || originalTasks.find(t => t.id === taskId);

const assertCurrentEdit = (
  state: ScheduleEditState,
  action: {
    type: string;
    payload: {taskId: number; shiftId?: number; taskSequence?: number};
  },
): TaskWithEditInfo => {
  const {taskId, shiftId, taskSequence} = action.payload;

  const current = assert(
    state.currentTaskEdit,
    `${action.type} action: currentTaskEdit must be set`,
    {taskId, shiftId, taskSequence},
  );

  assert(
    current.id === taskId,
    'Begin hover / hover operation: currentTaskEdit.id must be same as given taskId',
    {currentId: current.id, taskId, shiftId, taskSequence},
  );

  return current;
};

// const logAction = (action: DragDropAction, state: ScheduleEditState) => {
//   console.log(
//     'dragDropReducer: ',
//     action.type,
//     //@ts-ignore
//     action.payload.taskId,
//     ' shift:',
//     //@ts-ignore
//     action.payload.shiftId,
//     ' seqNo:',
//     //@ts-ignore
//     action.payload.taskSequence,
//     ', state:',
//     formatState(state),
//   );
// };

// const formatState = (state: ScheduleEditState): string => {
//   const minEdited = Object.keys(state.editedTasks).reduce((acc, k) => {
//     const key = parseInt(k);
//     //@ts-ignore
//     acc[k] = {
//       id: state.editedTasks[key].id,
//       shiftId: state.editedTasks[key].shiftId,
//       seqNo: state.editedTasks[key].taskSequence,
//       editMode: state.editedTasks[key].editMode,
//     };
//     return acc;
//   }, {});
//   const minCurrent = state.currentTaskEdit
//     ? {
//         id: state.currentTaskEdit.id,
//         shiftId: state.currentTaskEdit.shiftId,
//         seqNo: state.currentTaskEdit.taskSequence,
//         editMode: state.currentTaskEdit.editMode,
//       }
//     : undefined;

//   return JSON.stringify({current: minCurrent, edited: minEdited});
// };
